// Transaction Row → App transform — shared between data provider wrapper and TransactionDao.

import { calculatePricing } from '../calc.ts';
import type { TransactionRow, Transaction } from '@shared/types/transaction.ts';

/**
 * Enrich a raw transaction row with computed load/unload pricing.
 * Skips recalculation if pricing is already set.
 * Pure function — no DB calls, no side effects.
 *
 * The Refine data provider's read transform calls this on every row
 * arriving from supabase-js — guarded at the call site (appDataProvider)
 * against null/undefined input so this contract stays Transaction-in /
 * Transaction-out for DAO consumers.
 */
export function enrichTransaction(row: TransactionRow): Transaction {
  const existing = row as Partial<Transaction>;
  return {
    ...row,
    load_pricing: existing.load_pricing ?? calculatePricing({
      unit_price: row.load_price,
      price_type: row.load_price_type,
      weight: row.unloaded_weight, // always use unloaded_weight — loaded_weight is deprecated
      currency: row.load_price_currency,
      vat_rate_code: row.load_price_vat_rate_code,
    }),
    unload_pricing: existing.unload_pricing ?? calculatePricing({
      unit_price: row.unload_price,
      price_type: row.unload_price_type,
      weight: row.unloaded_weight,
      currency: row.unload_price_currency,
      vat_rate_code: row.unload_price_vat_rate_code,
    }),
  };
}
